import { ProductSourcingRuleMode } from '@clubhub/ignite/models/products';
import {
  ProductSourcingConditionType,
} from '@clubhub/ignite/models/products/product-sourcing';
import { ProductFeeType } from '@shared/constants';
import { OutcomeResults } from '@shared/models/enums/outcome-results';
import { LenderCapitalRestPeriod } from '@shared/models/lenders';
import { ResiBtlProductRate } from './asanto-product-upload';
import { CustomFee } from './custom-product-calculations';
import { ConditionalFeeScale } from './fee-scales';
import { MortgageType } from './mortgage-type';
import { ProductTransferInfoModel } from './products-transfer-info';
import { RepaymentMethod } from './repayment-method';

export enum Availability {
  AlsoAvailable = 1,
  OnlyAvailable = 2,
  No = 3,
  NotApplicable = 4,
}

export enum ProductErcChargeBasis {
  OutstandingBalance = 0,
  OriginalLoanAmount = 1,
}

export enum ProductTypeString {
  Fixed = 'Fixed',
  Discount = 'Discount',
  Variable = 'Variable',
  Tracker = 'Tracker',
  Lifetime = 'Lifetime',
}

export enum ArrangementFeeType {
  Loan = 1,
  Property = 2,
}

export interface ResiBtlProductErc {
  rate: number;
  until: string | null;
}

export enum CashbackStructure {
  Flat = 0,
  PercentageLoanAmount = 1,
}

export interface Product {
  lender: string;
  lenderCode: string;
  lenderNotes: string | null;
  lenderLogoUrl: string;
  productCode: string;
  productDescription: string;
  lendingType: string;
  withdrawalDate: string | null;
  isExclusive: boolean;
  isSecondCharge: boolean;
  direct: boolean | null;
  isAvailableFromPackagers: boolean;
  minLtv: number | null;
  maxLtv: number | null;
  repaymentTypes: RepaymentMethod[] | null;
  minLoanPolicy: number | null;
  maxLoanPolicy: number | null;
  appType: string | null;
  lenderAllowsTopSlicing: boolean;
  mortgageType: MortgageType[] | null;

  expatNotInUk: Availability | null;
  helpToBuy: Availability | null;
  hmo: Availability | null;
  holidayLetAirBb: Availability | null;
  jointBorrowerSoleProprietor: Availability | null;
  letToBuy: Availability | null;
  limitedCompanyBuyToLet: Availability | null;
  portfolioLandlord: Availability | null;
  regulatedBuyToLet: Availability | null;
  retirementInterestOnlyRio: boolean | null;
  rightToBuy: Availability | null;
  secondResidential: Availability | null;
  selfBuild: Availability | null;
  sharedOwnership: Availability | null;
  newBuild: Availability | null;
  greenEco: Availability | null;
  multiUnitFreeholdBlock: Availability | null;
  sharedEquity: Availability | null;
  offset: Availability | null;
  existingCustomer?: Availability | null;
  productTransfer?: Availability | null;
  firstTimeBuyer?: Availability | null;
  retroFit?: Availability | null;
  ownNew?: Availability | null;
  floorRate: number | null;
  cashback: number | null;
  purchaseCashback: number | null;
  purchaseCashbackRaw: number | null;
  purchaseCashbackStructure: CashbackStructure;
  remortgageCashback: number | null;
  remortgageCashbackRaw: number | null;
  remortgageCashbackStructure: CashbackStructure;
  freeLegal: boolean | null;
  freeVal: boolean | null;
  overpay: string | null;
  portable: boolean | null;
  overpaymentsAllowed: boolean | null;

  revertRate: number | null;
  revertRateType: string | null;
  revertRateMargin: number | null;
  rates: ResiBtlProductRate[];
  earlyRepaymentChargesErcApplied: boolean | null;
  ercRanks: ResiBtlProductErc[];
  ercChargeBasis: ProductErcChargeBasis | null;
  fees: Partial<Record<ProductFeeType, number>>;
  brokerFees?: CustomFee[];
  assumedLegalFees?: CustomFee[];
  isValuationFeeScaled: boolean;
  isValuationFeeRefer: boolean;

  aprc: number | null;
  trueCost: number | null;
  trueCostInitialPeriod: number | null;
  trueCostCustomPeriod: number | null;
  trueCostErc?: number;
  monthlyPaymentRevert: number | null;
  initialMonthlyPayment: number | null;
  totalInterestPayable: number | null;
  initialMonthlyPaymentIncFees: number | null;
  initialMonthlyPaymentInterestOnly: number | null;
  monthlyPaymentRevertInterestOnly: number | null;
  outstandingBalance: number | null;

  uniqueProductReference: string;
  productTransferEligible?: boolean;

  packager: string | null;
  lenderOnboard?: boolean;
  packagerOnboard?: boolean;
  ninetyDayValue?: number;
  hundredEightyDayValue?: number;
  notes: string | null;
  id: number | null;
  valRefundProductIncentive: boolean | null;
  valRefundProductIncentiveAmount: number | null;
  followOnDescription?: number | null;
  lenderOutcomeResult?: OutcomeResults;
  lenderAffordabilityMet?: '' | 'Pass' | 'Fail';

  baseValues?: Partial<Omit<Product, 'baseValues'>>;

  initialPeriod: number | null;
  initialPeriodUntil: string | null;
  initialRateValue: number | null;
  initialRateType: string | null;
  totalFees: number;
  forTerm: boolean | null;
  maximumAffordableLoanAmount: number | null;
  requiredMonthlyRentalIncome: number | null;
  stressRate: number | null;
  delayedStart: number | null;
  capitalRestPeriod: LenderCapitalRestPeriod;
}

export interface ProductSourcingRule {
  name: string;
  condition: ProductSourcingConditionType;
  mode: ProductSourcingRuleMode;
  someLending: boolean;
  overrides: Partial<Product>;

  /** If `true`, this product rule was applied on the backend already and does not need to be re-applied. */
  isBackendRule: boolean;
}

export interface MatchedLenderProducts {
  lenderName: string;
  lenderLogoUrl: string;
  products: Product[];
  initialRateFrom: number | null;
  trueCostFrom: number | null;
  trueCostInitialPeriodFrom: number | null;
  monthlyPaymentFrom: number | null;
  updatedDate: string;
  isSecondCharge: boolean;
  lenderFeeScales: ConditionalFeeScale<ProductFeeType>[];
  sourcingRules: ProductSourcingRule[];
  outcomeResult?: OutcomeResults;
  packagers: string[];
}

export interface LenderProductDetailsViewModel {
  lenderName: string;
  lenderUrl: string;
  affordabilityCalculatorUrl: string;
  buildingName: string;
  city: string;
  state: string;
  country: string;
  postcode: string;
  telephone: string;
  applicantMinimumAgeRange: string | null;
  applicantMaximumAgeRange: string | null;
  lastUpdated: string | null;
  baseRate: number;
  fullRateDescriptionParts: string[] | null;
  capitalRestPeriod: LenderCapitalRestPeriod;
}

export interface LenderProductTransferViewModel extends ProductTransferInfoModel {
  products: Product[];
  errorMessage: string;
  infoMessage: string;
  lenderName: string;
  initialPeriodDate: Date | string;
  /**
   * Set when the customer isn't eligible for a product transfer with this lender yet: the date they will be.
   * While it is set, the lender's products are not marked as transfers and its transfer-only products are hidden.
   */
  notEligibleUntil?: Date;
}
